import {createFileRoute} from '@tanstack/react-router';
import {useEffect, useMemo, useRef, useState} from 'react';
import {addToast} from '@heroui/toast';
import {apiAction} from '../api';
import services, {type BuildConfig, type BuildPicture} from '../services';
import PButton from '../components/PButton';
import {ClubType} from '../constants';
import {useJwtAuth, useJwtUser} from '../JWTAuthProvider';
import {startGoogleSignIn} from '../utilities/googleAuth.ts';
import {isTeamBasedSport, sportsForSurface, useSports} from '../sports/registry.ts';
import {sportAreaBase} from '../sports/sportAreas.ts';
import {needsOrganizerCompletion} from '../start/generatorHandoff.ts';
import {PRICING_FACTS_FALLBACK} from '../pricing/plans';
import {
    type BuildAccess, type BuildRoute, countLine, entryUnit, FORMAT_LABELS, fieldSize, managePath, organizerPathFor,
    parseField, quizRedirectUrl, readEntryParams, splashLine,
    restoreAnswers, todayIso,
} from '../build/buildRules';

/**
 * /build/ — draw on screen, share link, sign-up last (BuildQuiz, behind build_quiz_enabled).
 *
 * Four questions, each prefilling something; the real draw (or the first matchday) built by the
 * product's engines against a provisional event; the event's public page and join link, live now;
 * sign-up claims the event. /quiz/ is untouched and runs beside it. With the switch off this page
 * hands the visitor to /quiz/ with the same query string.
 */
export const Route = createFileRoute('/build')({
    component: BuildPage,
});

type Step = 'loading' | 'splash' | 1 | 2 | 3 | 4 | 5 | 6 | 7;

type Answers = {
    route?: BuildRoute;
    sport?: string;
    access?: BuildAccess;
    doubles?: boolean;
    field: string;
    title: string;
    date: string;
    venue: string;
    entryFee: string;
    currency: string;
};

type Saved = {key: string; token?: string; step?: Step; answers?: Answers; entry?: Record<string, string>; pendingClaim?: boolean};

const STORE = 'sp_build_state';
const EMPTY: Answers = {field: '', title: '', date: '', venue: '', entryFee: '', currency: 'USD'};
const ROUTE_OPTIONS: {val: BuildRoute; label: string; sub: string}[] = [
    {val: 'tournament', label: 'A tournament', sub: 'One day or a weekend'},
    {val: 'league', label: 'A league', sub: 'A season of matchdays'},
    {val: 'session', label: 'A weekly session', sub: 'The same night every week'},
    {val: 'social', label: 'A social', sub: 'Americano, Mexicano, King of the Court'},
];
const ACCESS_OPTIONS: {val: BuildAccess; label: string; sub: string}[] = [
    {val: 'add', label: "I'll add them", sub: 'Type the names or just how many'},
    {val: 'link', label: 'They register through a link', sub: 'Name and phone, editable later'},
    {val: 'pay', label: 'They register and pay', sub: 'Set the entry fee now; connect payouts after'},
];

function uuid(): string {
    try {
        return crypto.randomUUID();
    } catch {
        return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
    }
}

function load(): Saved | null {
    try {
        return JSON.parse(localStorage.getItem(STORE) || 'null');
    } catch {
        return null;
    }
}

function save(s: Saved): void {
    try {
        localStorage.setItem(STORE, JSON.stringify(s));
    } catch {
        // private mode: the page still works for this visit
    }
}

function BuildPage() {
    const jwt = useJwtAuth();
    const user = useJwtUser() as any;
    useSports(); // the registry refresh, so the sport list is the live one
    const [config, setConfig] = useState<BuildConfig | null>(null);
    const [step, setStep] = useState<Step>('loading');
    const [key, setKey] = useState('');
    const [entry, setEntry] = useState<Record<string, string>>({});
    const [answers, setAnswers] = useState<Answers>(EMPTY);
    const [token, setToken] = useState<string | null>(null);
    const [picture, setPicture] = useState<BuildPicture | null>(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [pendingClaim, setPendingClaim] = useState(false);
    const started = useRef(false);

    const facts = config?.pricing ?? PRICING_FACTS_FALLBACK;
    const sports = useMemo(() => sportsForSurface('quiz'), [config]);

    // Persist the funnel (the Google round trip, a reload, the admin link from another browser).
    useEffect(() => {
        if (!key || step === 'loading') return;
        save({key, token: token ?? undefined, step, answers, entry, pendingClaim});
    }, [key, token, step, answers, entry, pendingClaim]);

    // First load: the switch, the URL params, a claim link, or where this browser left off.
    useEffect(() => {
        let gone = false;
        (async () => {
            const cfg = await services.build.config().catch(() => null);
            if (gone) return;
            if (!cfg?.enabled) {
                window.location.replace(quizRedirectUrl(window.location.search));
                return;
            }
            setConfig(cfg);
            const params = readEntryParams(window.location.search);
            const claimToken = new URLSearchParams(window.location.search).get('claim');
            const saved = load();
            const k = saved?.key || uuid();
            setKey(k);
            setEntry(saved?.entry && Object.keys(saved.entry).length ? saved.entry : params);
            // Everything but the date comes back: a date typed on an earlier visit must never date today's event.
            if (saved?.answers) setAnswers(restoreAnswers(saved.answers, EMPTY));
            const t = claimToken || saved?.token || null;
            if (t) {
                setToken(t);
                const pic = await services.build.state(t).catch(() => null);
                if (pic && !pic.claimed) {
                    setPicture(pic);
                    setPendingClaim(!!saved?.pendingClaim && !claimToken);
                    setStep(saved?.pendingClaim && !claimToken ? 7 : (claimToken ? 6 : ((saved?.step as Step) ?? 5)));
                    return;
                }
                // Claimed or purged: start afresh.
                setToken(null);
            }
            setStep('splash');
        })();
        return () => { gone = true; };
    }, []);

    // The share step watches names arrive: players who join take the numbered places in the draw.
    useEffect(() => {
        if (step !== 6 || !token) return;
        const timer = window.setInterval(() => {
            services.build.state(token).then(setPicture).catch(() => {});
        }, 10_000);
        return () => window.clearInterval(timer);
    }, [step, token]);

    // Back from Google with a token to claim: finish the organizer sign-up, then claim.
    useEffect(() => {
        if (!jwt.initialized || !user || !pendingClaim || !token || step !== 7 || busy) return;
        void completeAndClaim();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jwt.initialized, user?.uid, pendingClaim, token, step]);

    const sport = sports.find((s) => s.key === answers.sport);
    const teamSport = answers.sport ? isTeamBasedSport(answers.sport) : false;
    const unit = entryUnit(teamSport, answers.route ?? 'tournament', !!answers.doubles);
    const field = parseField(answers.field);
    const entries = fieldSize(field) * unit.perEntry;

    const beacon = (event: 'quiz_started' | 'question_answered' | 'link_shared', extra: {step?: number; channel?: string} = {}) =>
        services.build.funnel({event, funnel: 'build', key, src: entry.src, sport: answers.sport, token: token ?? undefined, ...extra});

    const answer = (n: 1 | 2 | 3, patch: Partial<Answers>) => {
        const next = {...answers, ...patch};
        if (n === 2 && patch.sport) {
            // Pairs by default where the sport plays doubles (the quiz's own default).
            next.doubles = ['padel', 'pickleball', 'pickelball'].includes(patch.sport);
        }
        setAnswers(next);
        beacon('question_answered', {step: n});
        setStep((n + 1) as Step);
    };

    const begin = () => {
        if (!started.current) {
            started.current = true;
            beacon('quiz_started');
        }
        // The URL's sport is a pre-answer, confirmed with one tap on question 2.
        const fromUrl = entry.sport?.toLowerCase();
        const match = fromUrl ? sports.find((s) => s.key === fromUrl || s.label.toLowerCase() === fromUrl || String(s.value).toLowerCase() === fromUrl) : undefined;
        if (match && !answers.sport) setAnswers((a) => ({...a, sport: match.key}));
        // ?format=league (or tournament / session / social) pre-answers question 1 the same way.
        const routeFromUrl = ROUTE_OPTIONS.find((o) => o.val === (entry.format || '').toLowerCase());
        if (routeFromUrl && !answers.route) setAnswers((a) => ({...a, route: routeFromUrl.val}));
        setStep(1);
    };

    const build = async () => {
        setError(null);
        if (!answers.route || !answers.sport || !answers.access) return;
        const n = fieldSize(field);
        if (n < (config?.min_entries ?? 3)) {
            setError(`Add at least ${config?.min_entries ?? 3} to build a draw.`);
            return;
        }
        if (answers.route === 'social' && (n < 4 || n % 4 !== 0)) {
            setError('A social format needs players in fours (4, 8, 12…).');
            return;
        }
        setBusy(true);
        try {
            const res = await services.build.start({
                key,
                route: answers.route,
                sport: answers.sport,
                access: answers.access,
                doubles: !!answers.doubles,
                count: field.count ?? undefined,
                names: field.names.length ? field.names : undefined,
                title: answers.title.trim() || undefined,
                date: answers.date || undefined,
                venue: answers.venue.trim() || undefined,
                entry_fee: answers.access === 'pay' && answers.entryFee ? Number(answers.entryFee) : undefined,
                currency: answers.access === 'pay' ? answers.currency : undefined,
                entry,
            });
            setToken(res.token);
            setPicture(res);
            beacon('question_answered', {step: 4});
            setStep(5);
        } catch (e: any) {
            setError(e?.message || e?.error || 'Could not build the draw. Try again.');
        }
        setBusy(false);
    };

    const switchFormat = async (format: string) => {
        if (!token) return;
        setBusy(true);
        try {
            setPicture(await services.build.draw(token, format));
        } catch (e: any) {
            addToast({color: 'danger', title: e?.message || e?.error || 'Could not change the format.'});
        }
        setBusy(false);
    };

    const share = async (channel: 'copy' | 'whatsapp' | 'copy_admin') => {
        if (!picture) return;
        beacon('link_shared', {channel});
        if (channel === 'whatsapp') {
            window.open(`https://wa.me/?text=${encodeURIComponent(picture.links.message)}`, '_blank', 'noopener');
            return;
        }
        const text = channel === 'copy_admin' ? (picture.links.admin ?? '') : picture.links.message;
        try {
            await navigator.clipboard.writeText(text);
            addToast({color: 'success', title: 'Copied'});
        } catch {
            addToast({color: 'warning', title: text});
        }
    };

    // ---- step 7: sign-up last ----
    const [suName, setSuName] = useState('');
    const [suEmail, setSuEmail] = useState('');
    const [suPassword, setSuPassword] = useState('');

    const signupBody = (profile: {email: string; password: string; name: string}) => ({
        type: 'organizer',
        landing_url: jwt.landingUrl,
        quiz_key: key,
        platform: 'web',
        profile: {...profile, lastname: '', inviter_uid: '', leaderboard_enrolled: true},
        questionnaire: {
            sport: answers.sport,
            elo_grade: null, how_long_playing: null, how_often: null, nationality: null,
            club: {type: ClubType.Community, name: `${(profile.name || 'My').trim()}'s Events`, country_code: '', city: '', city_coordinates: null, meta: {}, organizer_path: organizerPathFor(answers.route ?? 'tournament')},
        },
    });

    const claim = async () => {
        if (!token) return;
        const res = await services.build.claim(token);
        setPendingClaim(false);
        try { localStorage.removeItem(STORE); } catch { /* fine */ }
        window.location.assign(managePath(res, sportAreaBase(res.sport_type)));
    };

    const completeAndClaim = async () => {
        setBusy(true);
        setError(null);
        try {
            if (needsOrganizerCompletion(user)) {
                const res: any = await apiAction('signup', signupBody({email: user.email, password: '', name: user.name || ''}));
                if (res?.error) throw new Error(res.error);
                jwt.setFieldsFromResponse(res);
            }
            await claim();
        } catch (e: any) {
            setError(e?.message || e?.error || 'Could not keep the event. Try again.');
            setBusy(false);
        }
    };

    const submitSignup = async (e: React.FormEvent) => {
        e.preventDefault();
        setBusy(true);
        setError(null);
        try {
            const res: any = await apiAction('signup', signupBody({email: suEmail.trim(), password: suPassword, name: suName.trim()}));
            if (res?.error) throw new Error(res.error);
            jwt.setFieldsFromResponse(res);
            await claim();
        } catch (err: any) {
            setError(err?.message || err?.error || 'Sign up failed. Please try again.');
            setBusy(false);
        }
    };

    const google = () => {
        setPendingClaim(true);
        save({key, token: token ?? undefined, step: 7, answers, entry, pendingClaim: true});
        startGoogleSignIn('/build/');
    };

    // ---- render ----
    const card = 'w-full rounded-2xl bg-white p-5 shadow-sm ring-1 ring-black/5';
    const option = (selected: boolean) => `w-full rounded-xl border-2 px-4 py-3 text-left transition ${selected ? 'border-[#082F8A] bg-[#EDEEF4]' : 'border-default-200 hover:border-[#082F8A]/50'}`;

    return (
        <div className="min-h-screen bg-[#F4F6FB] px-4 py-6 text-[#1a1a2e]">
            <title>Build your event in 30 seconds — ScorePal</title>
            <meta name="robots" content="noindex" />
            <div className="mx-auto flex max-w-xl flex-col gap-4">
                <div className="flex items-center gap-2 print:hidden">
                    <img src="/favicon-180x180.png" alt="ScorePal" width={32} height={32} className="rounded-lg" />
                    <span className="font-semibold">ScorePal</span>
                    {typeof step === 'number' && step <= 7 && <span className="ml-auto text-xs text-default-500">Step {step} of 7</span>}
                </div>

                {step === 'loading' && <div className={card}>Loading…</div>}

                {step === 'splash' && (
                    <div className={card}>
                        <h1 className="text-2xl font-bold leading-tight">{splashLine(entry.sport, entry.format, facts)}</h1>
                        <p className="mt-2 text-sm text-default-600">Four questions, then your real draw on screen — before you sign up.</p>
                        <PButton color="primary" className="mt-4 w-full" onPress={begin}>Start</PButton>
                    </div>
                )}

                {step === 1 && (
                    <div className={card}>
                        <h2 className="text-xl font-bold">What are you running?</h2>
                        {answers.route && (
                            <PButton color="primary" className="mt-3 w-full" onPress={() => answer(1, {route: answers.route})}>
                                Continue with {ROUTE_OPTIONS.find((o) => o.val === answers.route)?.label.toLowerCase()}
                            </PButton>
                        )}
                        <div className="mt-3 grid gap-2">
                            {ROUTE_OPTIONS.map((o) => (
                                <button key={o.val} type="button" className={option(answers.route === o.val)} onClick={() => answer(1, {route: o.val})}>
                                    <div className="font-semibold">{o.label}</div>
                                    <div className="text-xs text-default-500">{o.sub}</div>
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {step === 2 && (
                    <div className={card}>
                        <h2 className="text-xl font-bold">Sport</h2>
                        {answers.sport && sport && (
                            <PButton color="primary" className="mt-3 w-full" onPress={() => answer(2, {sport: answers.sport})}>
                                Continue with {sport.label}
                            </PButton>
                        )}
                        <div className="mt-3 grid grid-cols-2 gap-2">
                            {sports.filter((s) => answers.route !== 'social' || !isTeamBasedSport(s.key)).map((s) => (
                                <button key={s.key} type="button" className={option(answers.sport === s.key)} onClick={() => answer(2, {sport: s.key})}>
                                    {s.label}
                                </button>
                            ))}
                        </div>
                        <button type="button" className="mt-3 text-sm underline" onClick={() => setStep(1)}>← Back</button>
                    </div>
                )}

                {step === 3 && (
                    <div className={card}>
                        <h2 className="text-xl font-bold">How do players get in?</h2>
                        <div className="mt-3 grid gap-2">
                            {ACCESS_OPTIONS.map((o) => (
                                <button key={o.val} type="button" className={option(answers.access === o.val)} onClick={() => answer(3, {access: o.val})}>
                                    <div className="font-semibold">{o.label}</div>
                                    <div className="text-xs text-default-500">{o.sub}</div>
                                </button>
                            ))}
                        </div>
                        <button type="button" className="mt-3 text-sm underline" onClick={() => setStep(2)}>← Back</button>
                    </div>
                )}

                {step === 4 && (
                    <div className={card}>
                        <h2 className="text-xl font-bold">How many {unit.noun}?</h2>
                        {!teamSport && answers.route !== 'social' && (
                            <div className="mt-2 inline-flex rounded-lg bg-default-100 p-1 text-sm">
                                {[false, true].map((d) => (
                                    <button key={String(d)} type="button" onClick={() => setAnswers({...answers, doubles: d})}
                                            className={`rounded-md px-3 py-1 ${!!answers.doubles === d ? 'bg-white shadow-sm' : 'text-default-500'}`}>
                                        {d ? 'Doubles (pairs)' : 'Singles'}
                                    </button>
                                ))}
                            </div>
                        )}
                        <textarea
                            className="mt-3 w-full rounded-xl border border-default-200 p-3 text-sm"
                            rows={5}
                            placeholder={`A number (e.g. 12), or the ${unit.noun}, one per line${unit.perEntry === 2 ? ' ("Ana & Ben")' : ''}`}
                            value={answers.field}
                            onChange={(e) => setAnswers({...answers, field: e.target.value})}
                        />
                        {entries > 0 && (
                            <p className="mt-1 text-sm font-medium text-[#082F8A]" data-testid="count-line">{countLine(entries, unit.unit, answers.route ?? 'tournament', facts)}</p>
                        )}
                        <div className="mt-3 grid gap-2 sm:grid-cols-2">
                            <input className="rounded-xl border border-default-200 p-2 text-sm" placeholder="Name (optional)" value={answers.title} onChange={(e) => setAnswers({...answers, title: e.target.value})} />
                            <input className="rounded-xl border border-default-200 p-2 text-sm" type="date" min={todayIso()} value={answers.date} onChange={(e) => setAnswers({...answers, date: e.target.value})} />
                            <input className="rounded-xl border border-default-200 p-2 text-sm sm:col-span-2" placeholder="Venue (optional)" value={answers.venue} onChange={(e) => setAnswers({...answers, venue: e.target.value})} />
                        </div>
                        {answers.access === 'pay' && (
                            <div className="mt-3 flex gap-2">
                                <input className="flex-1 rounded-xl border border-default-200 p-2 text-sm" type="number" min={0} step="0.01" placeholder="Entry fee" value={answers.entryFee}
                                       onChange={(e) => setAnswers({...answers, entryFee: e.target.value})} />
                                <select className="rounded-xl border border-default-200 p-2 text-sm" value={answers.currency} onChange={(e) => setAnswers({...answers, currency: e.target.value})}>
                                    {['USD', 'GBP', 'EUR', 'AED', 'AUD', 'CAD'].map((c) => <option key={c}>{c}</option>)}
                                </select>
                            </div>
                        )}
                        {error && <p className="mt-2 text-sm text-danger">{error}</p>}
                        <PButton color="primary" className="mt-4 w-full" isLoading={busy} isDisabled={entries < 1} onPress={build}>
                            {answers.route === 'league' || answers.route === 'session' ? 'Show my fixtures' : 'Build my draw'}
                        </PButton>
                        <button type="button" className="mt-3 text-sm underline" onClick={() => setStep(3)}>← Back</button>
                    </div>
                )}

                {step === 5 && picture && (
                    <div className={card}>
                        <div className="flex items-start justify-between gap-2">
                            <div>
                                <h2 className="text-xl font-bold">{picture.event.title}</h2>
                                <p className="text-xs text-default-500">{picture.entries} {picture.unit} · {countLine(picture.entries, picture.unit, picture.route, facts)}</p>
                            </div>
                            <PButton size="sm" variant="flat" className="print:hidden" onPress={() => window.print()}>Print</PButton>
                        </div>
                        {picture.formats.length > 1 && (
                            <div className="mt-3 flex flex-wrap gap-2 print:hidden">
                                {picture.formats.map((f) => (
                                    <button key={f} type="button" disabled={busy} onClick={() => switchFormat(f)}
                                            className={`rounded-full border px-3 py-1 text-sm ${picture.format === f ? 'border-[#082F8A] bg-[#082F8A] text-white' : 'border-default-200'}`}>
                                        {FORMAT_LABELS[f] ?? f}
                                    </button>
                                ))}
                                <button type="button" disabled={busy} className="rounded-full border border-default-200 px-3 py-1 text-sm" onClick={() => switchFormat(picture.format ?? '')}>
                                    Regenerate
                                </button>
                            </div>
                        )}
                        <DrawView picture={picture} />
                        <PButton color="primary" className="mt-4 w-full print:hidden" onPress={() => setStep(6)}>Share it</PButton>
                    </div>
                )}

                {step === 6 && picture && (
                    <div className={card}>
                        <h2 className="text-xl font-bold">Share this so your players can join and see the draw.</h2>
                        <div className="mt-3 rounded-xl bg-default-100 p-3 text-sm whitespace-pre-line break-all" data-testid="share-message">{picture.links.message}</div>
                        <div className="mt-3 grid gap-2 sm:grid-cols-2">
                            <PButton color="success" onPress={() => share('whatsapp')}>Send on WhatsApp</PButton>
                            <PButton variant="flat" onPress={() => share('copy')}>Copy link</PButton>
                        </div>
                        <p className="mt-3 text-xs text-default-500">
                            Public page: <a className="underline" href={picture.links.public} target="_blank" rel="noreferrer">{picture.links.public}</a>
                        </p>
                        {picture.links.admin && (
                            <p className="mt-2 text-xs text-default-500">
                                Your admin link (keeps this event yours on any device for {config?.provisional_days ?? 7} days):{' '}
                                <button type="button" className="underline" onClick={() => share('copy_admin')}>copy it</button>
                            </p>
                        )}
                        {picture.registrations > 0 && <p className="mt-2 text-sm font-medium text-success">{picture.registrations} player{picture.registrations === 1 ? ' has' : 's have'} joined.</p>}
                        <div className="mt-3" data-testid="whos-in">
                            <div className="text-xs font-semibold uppercase tracking-wide text-default-500">
                                Who's in{picture.open_slots > 0 ? ` · ${picture.open_slots} place${picture.open_slots === 1 ? '' : 's'} to fill` : ''}
                            </div>
                            <ul className="mt-1 flex flex-wrap gap-1.5">
                                {(picture.field_items ?? []).map((item, i) => (
                                    <li key={item.name + i} className={`rounded-full px-2 py-0.5 text-xs ${item.placeholder ? 'border border-dashed border-default-300 text-default-400' : 'bg-success-100 text-success-700'}`}>
                                        {item.name}
                                    </li>
                                ))}
                            </ul>
                            <p className="mt-1 text-xs text-default-500">Players who join take the numbered places in your draw — it updates as they arrive.</p>
                        </div>
                        <PButton color="primary" className="mt-4 w-full" onPress={() => setStep(7)}>Keep it — free</PButton>
                        <button type="button" className="mt-3 text-sm underline" onClick={() => setStep(5)}>← Back to the draw</button>
                    </div>
                )}

                {step === 7 && (
                    <div className={card}>
                        <h2 className="text-xl font-bold">Keep your event</h2>
                        <p className="mt-1 text-sm text-default-600">Create your free account and it is yours — players who already joined stay in.</p>
                        {error && <p className="mt-2 text-sm text-danger">{error}</p>}
                        {user && !needsOrganizerCompletion(user) ? (
                            <PButton color="primary" className="mt-4 w-full" isLoading={busy} onPress={completeAndClaim}>Keep it as {user.email}</PButton>
                        ) : user ? (
                            <PButton color="primary" className="mt-4 w-full" isLoading={busy} onPress={completeAndClaim}>Finish signing up and keep it</PButton>
                        ) : (
                            <>
                                <PButton variant="bordered" className="mt-4 w-full" onPress={google} isDisabled={busy}>Continue with Google</PButton>
                                <form className="mt-3 grid gap-2" onSubmit={submitSignup}>
                                    <input className="rounded-xl border border-default-200 p-2 text-sm" placeholder="Your name" required value={suName} onChange={(e) => setSuName(e.target.value)} />
                                    <input className="rounded-xl border border-default-200 p-2 text-sm" type="email" placeholder="Email" required value={suEmail} onChange={(e) => setSuEmail(e.target.value)} />
                                    <input className="rounded-xl border border-default-200 p-2 text-sm" type="password" placeholder="Password" required minLength={8} value={suPassword} onChange={(e) => setSuPassword(e.target.value)} />
                                    <PButton color="primary" type="submit" isLoading={busy}>Create my account</PButton>
                                </form>
                            </>
                        )}
                        <button type="button" className="mt-3 text-sm underline" onClick={() => setStep(6)}>← Back</button>
                    </div>
                )}
            </div>
        </div>
    );
}

/** The draw (groups and rounds), the first matchday, or a social night's rounds, as the server built them. */
function DrawView({picture}: {picture: BuildPicture}) {
    return (
        <div className="mt-4 grid gap-4" data-testid="build-draw">
            {picture.sections.map((section) => (
                <div key={section.title}>
                    <h3 className="text-sm font-bold uppercase tracking-wide text-[#082F8A]">{section.title}</h3>
                    {section.rounds.map((round) => (
                        <div key={round.title} className="mt-2">
                            <div className="text-xs font-semibold text-default-500">{round.title}</div>
                            <ul className="mt-1 grid gap-1">
                                {round.matches.map((m, i) => (
                                    <li key={i} className="flex items-center gap-2 rounded-lg border border-default-100 px-2 py-1 text-sm">
                                        <span className="min-w-0 flex-1 truncate">{m.a}</span>
                                        <span className="text-xs text-default-400">{m.court ? `Court ${m.court}` : 'vs'}</span>
                                        <span className="min-w-0 flex-1 truncate text-right">{m.b}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            ))}
            {picture.kind === 'league' && picture.route !== 'social' && (
                <div>
                    <h3 className="text-sm font-bold uppercase tracking-wide text-[#082F8A]">Table</h3>
                    <ol className="mt-1 grid gap-1 text-sm">
                        {picture.field.map((name, i) => (
                            <li key={name + i} className="flex justify-between rounded-lg border border-default-100 px-2 py-1">
                                <span>{i + 1}. {name}</span><span className="text-default-400">0 pts</span>
                            </li>
                        ))}
                    </ol>
                </div>
            )}
        </div>
    );
}
